import React, { useState, useEffect } from 'react';
import { tenantNurseAPI } from '../../../services/api/tenantNurseAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  UserCheck, 
  Heart, 
  FileText, 
  Search, 
  Eye,
  Plus,
  Calendar,
  Mail,
  Phone,
  Activity,
  AlertTriangle,
  TrendingUp,
  Clock
} from 'lucide-react';

const PatientCare = () => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    filterPatients();
  }, [searchTerm, patients]);

  // Add Esc key handlers for modals
  useEscKeyHandler(showPatientModal, () => setShowPatientModal(false));
  useEscKeyHandler(showNotesModal, () => setShowNotesModal(false));

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await tenantNurseAPI.getPatients();
      setPatients(response.data);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const filterPatients = () => {
    if (!searchTerm) {
      setFilteredPatients(patients);
    } else {
      const filtered = patients.filter(patient =>
        patient.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        patient.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
    }
  };

  const handleViewPatient = async (patient) => {
    try {
      const response = await tenantNurseAPI.getPatientDetails(patient.id);
      setSelectedPatient(response.data);
      setShowPatientModal(true);
    } catch (error) {
      console.error('Failed to load patient details:', error);
      toast.error('Failed to load patient details');
    }
  };

  const handleAddNotes = (patient) => {
    setSelectedPatient(patient);
    setShowNotesModal(true);
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      return age - 1;
    }
    return age;
  };

  const PatientDetailsModal = () => {
    if (!selectedPatient) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Patient Care Details</h2>
              <button
                onClick={() => setShowPatientModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Patient Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Patient Information</h3>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {selectedPatient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                      </span>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">{selectedPatient.alias}</h4>
                      <p className="text-sm text-gray-600">Patient ID: {selectedPatient.id.slice(-8)}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Age</label>
                      <p className="text-sm text-gray-900">{calculateAge(selectedPatient.dob)} years</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Gender</label>
                      <p className="text-sm text-gray-900">{selectedPatient.gender || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Status</label>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {selectedPatient.status}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Registered</label>
                      <p className="text-sm text-gray-900">
                        {new Date(selectedPatient.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{selectedPatient.email}</span>
                    </div>
                    {selectedPatient.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{selectedPatient.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Vitals */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Recent Vital Signs</h3>
                
                {selectedPatient.recent_vitals && selectedPatient.recent_vitals.length > 0 ? (
                  <div className="space-y-3">
                    {selectedPatient.recent_vitals.map((vital, index) => (
                      <div key={vital.id} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(vital.created_at).toLocaleDateString()}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            vital.quality_flag === 'High' ? 'bg-green-100 text-green-800' :
                            vital.quality_flag === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {vital.quality_flag || 'Normal'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Heart Rate:</span>
                            <span className="ml-2 text-gray-900">{vital.heart_rate || 'N/A'} bpm</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Rhythm:</span>
                            <span className={`ml-2 ${
                              vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(vital.rhythm) 
                                ? 'text-red-600 font-medium' 
                                : 'text-gray-900'
                            }`}>
                              {vital.rhythm || 'Normal'}
                            </span>
                          </div>
                          {vital.blood_pressure && (
                            <div className="col-span-2">
                              <span className="text-gray-600">Blood Pressure:</span>
                              <span className="ml-2 text-gray-900">{vital.blood_pressure} mmHg</span>
                            </div>
                          )}
                        </div>
                        {vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(vital.rhythm) && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                            <div className="flex items-center space-x-1">
                              <AlertTriangle className="h-4 w-4 text-red-600" />
                              <span className="text-sm font-medium text-red-800">Requires attention</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-8 rounded-lg text-center">
                    <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No recent vital signs recorded</p>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
              <button
                onClick={() => setShowPatientModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowPatientModal(false);
                  handleAddNotes(selectedPatient);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700"
              >
                Add Care Notes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CareNotesModal = () => {
    const [careNotes, setCareNotes] = useState('');
    const [careType, setCareType] = useState('assessment');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      try {
        setSaving(true);
        await tenantNurseAPI.addCareNotes(selectedPatient.id, careNotes, careType);
        toast.success('Care notes added successfully');
        setShowNotesModal(false);
        setCareNotes('');
        setCareType('assessment');
      } catch (error) {
        console.error('Failed to add care notes:', error);
        toast.error('Failed to add care notes');
      } finally {
        setSaving(false);
      }
    };

    if (!selectedPatient) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-2xl w-full">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Add Care Notes</h2>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {/* Patient Info */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium text-gray-900">Patient: {selectedPatient.alias}</p>
              <p className="text-sm text-gray-600">ID: {selectedPatient.id.slice(-8)}</p>
            </div>

            {/* Care Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Care Type
              </label>
              <select
                value={careType}
                onChange={(e) => setCareType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="assessment">Assessment</option>
                <option value="intervention">Intervention</option>
                <option value="observation">Observation</option>
                <option value="medication">Medication</option>
              </select>
            </div>

            {/* Care Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Care Notes
              </label>
              <textarea
                value={careNotes}
                onChange={(e) => setCareNotes(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter detailed care notes..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !careNotes.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Care Notes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Patient Care</h1>
          <p className="text-gray-600">Monitor patient health and provide comprehensive care</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100 mr-4">
              <UserCheck className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Patients</p>
              <p className="text-2xl font-bold text-gray-900">{patients.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100 mr-4">
              <Heart className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">With Recent Vitals</p>
              <p className="text-2xl font-bold text-gray-900">
                {patients.filter(p => p.latest_vital).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-100 mr-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Concerning Vitals</p>
              <p className="text-2xl font-bold text-gray-900">
                {patients.filter(p => 
                  p.latest_vital && p.latest_vital.rhythm && 
                  ['Irregular', 'Atrial Fibrillation'].includes(p.latest_vital.rhythm)
                ).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-100 mr-4">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Care Notes Today</p>
              <p className="text-2xl font-bold text-gray-900">8</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search patients by name, email, or alias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>
      </div>

      {/* Patients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPatients.map((patient) => (
          <div key={patient.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {patient.alias?.split(' ').map(n => n[0]).join('') || 'P'}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{patient.alias}</h3>
                    <p className="text-sm text-gray-500">
                      {patient.gender && patient.dob ? 
                        `${patient.gender}, ${calculateAge(patient.dob)} years` : 
                        'Information incomplete'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Vital Status Indicator */}
                {patient.latest_vital ? (
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      patient.latest_vital.quality_flag === 'High' ? 'bg-green-100 text-green-800' :
                      patient.latest_vital.quality_flag === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {patient.latest_vital.quality_flag || 'Normal'}
                    </span>
                  </div>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    No vitals
                  </span>
                )}
              </div>

              {/* Latest Vitals Summary */}
              {patient.latest_vital ? (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">Latest Vitals</span>
                    <span className="text-xs text-gray-500">
                      {new Date(patient.latest_vital.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-600">HR:</span>
                      <span className="ml-1 text-gray-900">{patient.latest_vital.heart_rate || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Rhythm:</span>
                      <span className={`ml-1 ${
                        patient.latest_vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm) 
                          ? 'text-red-600 font-medium' 
                          : 'text-gray-900'
                      }`}>
                        {patient.latest_vital.rhythm || 'Normal'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Alert for concerning vitals */}
                  {patient.latest_vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(patient.latest_vital.rhythm) && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                      <div className="flex items-center space-x-1">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <span className="text-xs font-medium text-red-800">Requires attention</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-800">No recent vitals recorded</span>
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{patient.email}</span>
                </div>
                {patient.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span>{patient.phone}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleViewPatient(patient)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Eye className="h-4 w-4" />
                  <span>View</span>
                </button>
                <button
                  onClick={() => handleAddNotes(patient)}
                  className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Notes</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredPatients.length === 0 && (
        <div className="text-center py-12">
          <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No patients found</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria' : 'No patients available for care'}
          </p>
        </div>
      )}

      {/* Modals */}
      {showPatientModal && <PatientDetailsModal />}
      {showNotesModal && <CareNotesModal />}
    </div>
  );
};

export default PatientCare;